<template>
    <div id="notificationsForm" class="mt-4 mb-4">
        <div class="w-full mx-auto">
            <div class="flex flex-wrap">
                <div class="md:w-1/2 w-full md:pr-10 pr-0 md:mb-0 mb-4">
                    <label class="form-label-class" for="email">Notification Email Address</label>
                    <div class="w-full flex justify-center bg-white py-1" :class="{'bg-yellow-100 p-1 rounded': isEmailEdit == 'email'}">
                        <input v-model="form.email" class="flex-1 form-input-inline-w-none mr-1" id="email" type="text" placeholder="Enter your email" :disabled="!isEmailEdit" :class="{'is-invalid': errors && errors.email}">
                        <template v-if="isEmailEdit">
                            <button @click.prevent="updateEmail()" class="btn btn-sm btn-yellow btn-shadow-none btn-icon mr-1" aria-label="Save">
                                <i class="far fa-save"></i>
                            </button>
                            <button @click.prevent="isEmailEdit=false" class="btn btn-sm btn-white-ghost btn-shadow-none btn-icon" aria-label="Close Save">
                                <i class="fas fa-times text-gray-700"></i>
                            </button>
                        </template>
                        <button v-else @click.prevent="isEmailEdit=true" class="btn btn-sm btn-yellow-ghost btn-shadow-none btn-icon" v-tooltip="{ content: 'Edit your email address', placement: 'auto-start', theme: 'yellow' }" aria-label="Edit Email">
                            <i class="far fa-envelope"></i>
                        </button>
                    </div>
                </div>
                <div class="md:w-1/2 w-full">
                    <label class="form-label-class" for="phone">
                        Notification Phone Number
                    </label>
                    <div v-if="validPhone" class="w-full flex justify-center p-1">
                        <span class="flex-1 mr-1" v-tooltip="{ content: 'To change your phone number, delete the existing one first.', placement: 'auto-start', theme: 'info' }">
                            <input id="phone" class="w-full form-input-inline-w-none" disabled type="text" v-model="form.phone" />
                        </span>
                        <button @click.prevent="modalType = 'delete'" class="btn btn-sm btn-red-ghost btn-shadow-none btn-icon" v-tooltip="{ content: 'Delete Notification Phone Number', placement: 'auto-start', theme: 'red' }" aria-label="Delete">
                            <i class="fas fa-times text-red-500"></i>
                        </button>
                    </div>
                    <div class="btn btn-blue text-white inline-block mt-1" @click="openActivateModal" v-else>Activate Text Notifications</div>

                    <div v-if="errors.phone" class="error">{{errors.phone}}</div>
                </div>
            </div>
        </div>

        <div class="w-full mx-auto mt-8">
            <div class="subsection flex flex-wrap">
                <div class="inline-block subsection-title"><i class="fas fa-mail-bulk mr-2"></i>Services</div>
            </div>

            <div class="w-full my-4 mb-4">
                <p>Please indicate your notification preferences for the following services by checking the boxes below. Your changes will be saved automatically.</p>
                <p>Additionally, please ensure that you have entered a valid phone number above to activate text notifications.</p>

                <div v-for="(gNotifications, gIndex) in notificationGroups" class="mt-6 mb-4" :key="gNotifications">
                    <p class="font-bold uppercase text-sm text-yellow-500">{{ gNotifications.display }}</p>
                    <div class="flex flex-wrap mt-2 pb-1 border-b border-gray-100" v-for="(notification, index) in gNotifications.notifications" :key="notification">
                        <div class="md:flex-1 w-full xs:mb-1">
                            <span class="text-base">{{ notification.displayName }}</span>
                        </div>
                        <fieldset v-if="hasTextNotifications(notification)" :disabled="!validPhone" class="inline-block">
                            <div class="w-full mr-4">
                                <label class="cursor-pointer" :class="{'text-gray-500': !validPhone}" :for="`sms-` + (gIndex + index)">
                                    <input @change="update(notification)" :id="`sms-` + (gIndex + index)" type="checkbox" v-model="notification.sms" :value="notification.sms" class="mr-px cursor-pointer">
                                    Text Message
                                </label>
                            </div>
                        </fieldset>
                        <div class="inline-block">
                            <div class="w-full">
                                <label class="cursor-pointer" :for="`email-` + (gIndex + index)">
                                    <input @change="update(notification)" :id="`email-` + (gIndex + index)" type="checkbox" v-model="notification.email" :value="notification.email" class="mr-px cursor-pointer">
                                    Email
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <modal id="notification-modal" @close="closeActivateModal" v-if="modalType === 'activate'">
            <template #header>
                <!-- Activation Process Header -->
                <span v-if="!activationConfirmed && !activationDeclined">
                    <span v-show="!inProgress">Activate Text Notifications</span>
                    <span v-show="inProgress">Activate Confirmation</span>
                </span>
                <!-- Post-Activation Header -->
                <span v-else>
                    <span v-if="activationConfirmed">Activation Successful</span>
                    <span v-else-if="activationDeclined">Activation Declined</span>
                </span>
            </template>

            <template #body>
                <!-- Activation Process Body -->
                <span v-if="!activationConfirmed && !activationDeclined">
                    <div class="max-w-xl" v-show="!inProgress">
                        <label class="block text-base" for="phone-number">Phone Number</label>
                        <div class="flex mt-1">
                            <input id="phone-number" placeholder="Ex. +13121234567" class="flex-1 form-input-inline-w-none" v-model="form.phone" type="text" @input="filterPhoneInput" />
                        </div>
                        <p class="text-xs text-gray-700 leading-tight mt-2 mb-4">Enter your phone number, beginning with a “+” sign, followed by the country and area code. For example, in the US, if your phone number is (312) 123-4567, please enter +13121234567.</p>
                        <div class="flex items-center mb-1">
                            <input type="checkbox" name="terms" v-model="form.agreeToTerms" :value="true">
                            <label class="ml-2 text-base" for="terms">I agree to the <a :href="termsRoute" target="_blank">Terms and Conditions</a></label>
                        </div>
                        <p class="text-xs leading-tight mb-4">
                            By checking this box, you agree to our terms and conditions. Standard messaging rates may apply. You can opt out at any time by replying STOP. The frequency of notifications may vary based on market conditions and positions taken.
                        </p>
                        <p class="error mb-2" v-if="errors.phoneValidation">
                            {{ errors.phoneValidation }}
                        </p>
                    </div>
                    <div class="max-w-xl" v-show="inProgress">
                        <p class="text-base text-red-500 leading-tight my-4"><strong>DO NOT CLOSE OR NAVIGATE AWAY FROM THIS WINDOW!</strong></p>
                        <p class="text-base leading-tight mb-2">A confirmation SMS has been sent to your phone number. To complete the activation, please check your messages and respond as follows:</p>
                        <ul class="list-style list-disc mb-2">
                            <li class="text-sm !mb-1"><strong>Text "YES" or "Y"</strong> to confirm.</li>
                            <li class="text-sm !mb-0"><strong>Text "NO" or "N"</strong> to decline.</li>
                        </ul>
                        <p class="text-base leading-tight mb-4">Once confirmed, you will start receiving notifications. If you do not receive the SMS, please verify your phone number and try again.</p>
                    </div>
                </span>
                <!-- Post-Activation Body -->
                <span v-else>
                    <span v-if="activationConfirmed">
                        <p class="text-base font-semibold leading-tight my-4">Text Notifications have been successfully activated! You will now receive notifications at the provided phone number.</p>
                        <p class="text-base leading-tight my-4">Stay tuned for important updates and alerts that will keep you informed. If you ever need to manage your notification preferences or update your contact details, you can do so through your profile or notification settings.</p>
                        <p class="text-base leading-tight my-4">Thank you for choosing to stay connected with us!</p>
                    </span>
                    <span v-else-if="activationDeclined">
                        <p class="text-base leading-tight my-4"><strong>Activation was declined.</strong> We understand that you may have changed your mind, and that’s perfectly fine. If you decide to enable Text Notifications in the future, you can easily activate them again by following the same process. We’re here to help you stay informed whenever you’re ready.</p>
                    </span>
                </span>
            </template>

            <template #footer>
                <!-- Activation Process Footer -->
                <span v-if="!activationConfirmed && !activationDeclined">
                    <div class="flex justify-center" v-show="!inProgress">
                        <div @click="closeActivateModal" class="mx-1 btn btn-red-ghost btn-sm">Cancel</div>
                        <div :class="{ 'btn-disabled': !(form.agreeToTerms && form.phone) || isActivate }" @click="onClickActivate" class="mx-1 btn btn-sm btn-blue">Activate</div>
                    </div>
                    <div class="flex justify-center" v-show="inProgress">
                        <div @click="closeActivateModal" class="mx-1 btn btn-red-ghost btn-sm">Cancel</div>
                        <div class="mx-1 btn btn-sm btn-blue">
                            <span class="flex items-center">
                                <i class="fas fa-spinner fa-spin !mr-2"></i>
                                Waiting for response...
                            </span>
                        </div>
                    </div>
                </span>
                <!-- Post-Activation Footer -->
                <span v-else>
                    <div class="flex justify-center">
                        <div @click="closeActivateModal" class="mx-1 btn btn-yellow btn-sm">Close</div>
                    </div>
                </span>
            </template>
        </modal>

        <modal id="notification-modal" @close="modalType = false" v-if="modalType === 'delete'">
            <template #header>
                <div class="text-red-500"><i class="fas fa-exclamation-circle"></i> Delete Phone Number</div>
            </template>

            <template #body>
                <p class="text-base my-3 leading-tight">
                    Deleting your phone number will deactivate all text notifications. You can re-add a phone number at any time if you wish to reactivate notifications.
                </p>

                <p class="text-base mb-3 font-semibold">
                    Are you sure you want to delete your phone number?
                </p>
            </template>

            <template #footer>
                <div class="flex justify-center">
                    <div @click="modalType = false" class="mx-1 btn btn-red-ghost btn-sm">Cancel</div>
                    <div @click="deletePhone" class="mx-1 btn btn-sm btn-red">Delete</div>
                </div>
            </template>

        </modal>

        <flash-message ref="flash" :seconds-to-display="3"></flash-message>
    </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { debounce } from 'lodash';
import useAlertSmsManager from '../mixins/AlertSmsManager';

const props = defineProps({
    notificationGroups: {
        required: true,
        type: [Array, Object],
    },
    phoneNumber: String,
    email: String,
});

const flash = ref(null);

const isEmailEdit = ref(false);
const modalType = ref(false);

const form = ref({
    phone: props.phoneNumber,
    email: props.email,
    agreeToTerms: false,
});

const termsRoute = window.route('terms-of-service');
const validPhone = ref(!!props.phoneNumber);

const errors = ref({
    phoneValidation: false,
    phone: false,
    email: false,
});

const isActivate = ref(false);
const inProgress = ref(false);
const activationConfirmed = ref(null);
const activationDeclined = ref(null);

// Destructure the SMS manager functions and statuses
const { createRegistration, readRegistration, subscribeRegistration, unsubscribeRegistration, validatePhoneNumber, statuses } = useAlertSmsManager();

const filterPhoneInput = (event) => {
    const input = event.target.value;
    const filteredInput = input.replace(/[^+\d]/g, '');
    form.value.phone = filteredInput.substring(0, 16);
};

// Function to check if a phone number's registration status is declined
const checkPhoneRegistration = async (phone) => {
    try {
        const registration = await readRegistration(phone);
        if (registration.status?.code === statuses.declined) {
            deletePhone();
        }
    } catch (error) {
        console.error('Error reading registration:', error);
    }
};

// Function to validate the phone number and handle any errors
const validatePhoneAndHandleErrors = async (phone) => {
    const validatedPhone = await validatePhoneNumber(phone);
    if (!validatedPhone) {
        errors.value.phoneValidation = 'Invalid phone number format.';
        return null;
    }
    return validatedPhone;
};

// Function to create a new registration and handle any errors
const createRegistrationAndHandleErrors = async (phone) => {
    const registration = await createRegistration(phone);
    if (!registration) {
        errors.value.phoneValidation = 'Error during activation process. Please try again later.';
        return null;
    }
    return registration;
};

// Function to handle updates to the registration status
const handleRegistrationUpdate = (registration) => {
    if (registration.status.code === statuses.confirmed) {
        // If the registration is confirmed
        savePhone(form.value.phone, true);
        activationConfirmed.value = true;
        activationDeclined.value = false;
        validPhone.value = true;

        // Stop the process and unsubscribe after confirmation
        inProgress.value = false;
        unsubscribeRegistration();

    } else if (registration.status.code === statuses.declined) {
        // If the registration is declined
        savePhone(null, true);
        form.value.phone = '';
        activationConfirmed.value = false;
        activationDeclined.value = true;
        validPhone.value = false;

        // Stop the process and unsubscribe after declination
        inProgress.value = false;
        unsubscribeRegistration();
    }
};

// Function to subscribe to registration status updates
const subscribeToRegistrationUpdates = async (phone) => {
    try {
        // Subscribe to updates and use the handler function
        await subscribeRegistration(phone, handleRegistrationUpdate);
    } catch (subscribeError) {
        console.error('Error during subscription process:', subscribeError);
        errors.value.phoneValidation = subscribeError.message || 'Failed to subscribe to registration updates. Please try again later.';
        inProgress.value = false;
        throw subscribeError;
    }
};

// Function to handle the activation process when the user clicks "Activate"
const onClickActivate = async () => {
    try {
        isActivate.value = true;

        // Validate the phone number and handle errors
        const validatedPhone = await validatePhoneAndHandleErrors(form.value.phone);
        if (!validatedPhone) {
            isActivate.value = false;
            return;
        }

        form.value.phone = validatedPhone;

        // Create a new registration and handle errors
        const registration = await createRegistrationAndHandleErrors(form.value.phone);
        if (!registration) {
            isActivate.value = false;
            return;
        }

        inProgress.value = true;

        // Subscribe to registration updates
        await subscribeToRegistrationUpdates(registration.target);
    } catch (error) {
        console.error('Error during activation process:', error);
        errors.value.phoneValidation = 'An unexpected error occurred during activation. Please try again later.';
        isActivate.value = false;
        inProgress.value = false;
    }
};

const updateEmail = () => {
    const route = window.route('main.profile.update');
    const formData = { ...form.value };
    formData.type = 'email';

    axios
        .post(route, formData)
        .then((response) => {
            flash.value.display('«Notification Email Address» updated!');
            isEmailEdit.value = false;
            errors.value.email = false;
        })
        .catch((error) => {
            errors.value.email = 'Error updating «Notification Email Address», please try again later.';
            flash.value.error(errors.value.email);
        });
};

const update = (notification) => {
    const route = window.route('main.profile.notifications.update');
    axios
        .post(route, notification)
        .then((response) => {
            flash.value.display('Successfully saved «Notification»!');
        })
        .catch((error) => {
            flash.value.error('Error saving «Notification»');
        });
};

const openModal = (type) => {
    modalType.value = type;
};

const closeModal = () => {
    modalType.value = false;
    form.value.agreeToTerms = false;
    isActivate.value = false;
    inProgress.value = false;
    activationConfirmed.value = false;
    activationDeclined.value = false;
    errors.value.phoneValidation = false;
    unsubscribeRegistration();
};

const openActivateModal = () => openModal('activate');

const closeActivateModal = () => closeModal();

const deletePhone = () => {
    savePhone(null)
        .then(() => {
            modalType.value = false;
            validPhone.value = false;
            form.value.phone = '';

            // TODO: need to update each products
            // // Handle updating notifications
            // let textNotifications = [];
            // Object.keys(notificationGroups)
            //     .forEach((notifType) => {
            //         const notifications = props.notificationGroups[notifType].filter(notification => {
            //             return notification.sms;
            //         });
            //         textNotifications = textNotifications.concat(notifications);
            //     });

            // textNotifications.map((mNotification) => {
            //     mNotification.sms = false;
            //     update(mNotification);
            // });
        });
};

const savePhone = (phoneNumber, withoutFlash) => {
    const route = window.route('main.profile.notifications.update-phone');

    return axios
        .post(route, { type: 'phone', phone: phoneNumber })
        .then((response) => {
            if (response.data.success) {
                if (!withoutFlash) {
                    flash.value.display('«Notification Phone Number» updated!');
                }
            } else {
                throw Error;
            }
        })
        .catch((error) => {
            flash.value.error('Something went wrong, please try again later');
        });
};

const hasTextNotifications = (notification) => {
    if (
        (notification.name === 'mwj' && notification.type === 'video') ||
        (notification.name === 'webinar' && notification.type === 'video') ||
        (notification.type === 'webinar')
    ) {
        return false;
    }

    return (notification.type === 'video') ||
        (notification.name === 'binary') ||
        (notification.name === 'price-alert') ||
        (notification.name === 'binary-vol-2') ||
        (notification.type === 'trade' && notification.name === 'pas');
};

// // Watch for changes to form.value.phone
// const debouncedCheckPhoneRegistration = debounce((newPhone) => {
//     if (newPhone && modalType.value !== 'activate') {
//         checkPhoneRegistration(newPhone);
//     }
// }, 300); // Debounce by 300ms

// watch(
//     () => form.value.phone,
//     (newPhone) => {
//         debouncedCheckPhoneRegistration(newPhone);
//     },
//     { immediate: true }
// );
</script>

<style lang="scss">
#notification-modal #modalWindowWrapper {
    width: 32rem;

    .spinner .spinner-child {
        background-color: #b3d8fd;
    }
}
</style>
